import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  styled,
} from '@mui/material';
import { useAddIntegration } from '../../../useQuery';
import { useNotification } from '../../../hooks';

const PillRadio = ({ value, label, checked, onClick }) => {
  return (
    <button
      onClick={() => onClick(value)}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer border ${
        checked
          ? 'bg-blue-50 text-blue-600 border-blue-100'
          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );
};

const IntegrationOptions = [
  { value: 'Jira', label: 'Jira' },
//   { value: 'ServiceNow', label: 'ServiceNow' },
//   { value: 'Azure Dev Ops', label: 'Azure DevOps' },
//   { value: 'Slack', label: 'Slack' },
//   { value: 'MS Teams Channel', label: 'MS Teams' },
];

const IntegrationTab = ({ suite }) => {
  const notify = useNotification();
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [exceUrl, setExceUrl] = useState('');
  const [issueObj, setIssueObj] = useState({
    logType: '',
    logLevel: '',
    logId: '',
    projectKey: '',
    message: '',
  });
  const [workItemIds, setWorkItemIds] = useState({});

  const { mutate: addIntegration, isPending } = useAddIntegration({
    onSuccess: () => {
      notify("Integration added successfully", "success");
      // Reset form
      setIssueObj({
        logType: '',
        logLevel: '',
        logId: '',
        projectKey: '',
        message: '',
      });
      setWorkItemIds({});
    },
    onError: (error) => {
      notify(error.message || "Failed to add integration", "error");
    }
  });

  useEffect(() => {
 
 
    const baseURL = `${window.location.origin}/api/execute?suiteId=${suite?.id}`;
    setExceUrl(baseURL);
  }, [suite]);

  const handleAddIntegration = () => {
    if (!suite?.id) {
      notify("Suite ID not found", "error");
      return;
    }

    if (!selectedIntegration) {
      notify("Please select an integration type", "error");
      return;
    }

    const integrationTypeMap = {
      "Jira": 3,
      "ServiceNow": 5,
      "Azure Dev Ops": 1,
      "Slack": 4,
      "MS Teams Channel": 2
    };

    const intType = integrationTypeMap[selectedIntegration];
    if (!intType) {
      notify("Invalid integration type", "error");
      return;
    }

    const integrationData = {
      msg: issueObj.message || null,
      intid: workItemIds[selectedIntegration] || "",
      logId: issueObj.logId || "new",
      intType,
      logType: issueObj.logType || "Task",
      logLevel: issueObj.logLevel || "SuitLevel",
      projectkey: issueObj.projectkey || ""
    };

    addIntegration({
      data: integrationData,
      testSuiteId: suite.id
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setIssueObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="font-apple py-4">
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">Integrations</p>

        <div className="flex items-start gap-4 flex-wrap">
          {IntegrationOptions.map((option) => (
            <div
              key={option.value}
              className="flex flex-col items-center gap-2 min-h-[70px]"
            >
              <PillRadio
                value={option.value}
                label={option.label}
                checked={selectedIntegration === option.value}
                onClick={setSelectedIntegration}
              />
              {selectedIntegration === option.value &&
                (option.value === 'Slack' || option.value === 'MS Teams Channel') && (
                  <input
                    type="text"
                    placeholder={option.value === 'Slack' ? 'ChannelURL' : 'TeamURL'}
                    value={workItemIds[option.value] || ''}
                    onChange={(e) =>
                      setWorkItemIds((prev) => ({
                        ...prev,
                        [option.value]: e.target.value,
                      }))
                    }
                    className="w-[120px] px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400 text-center"
                  />
                )}
            </div>
          ))}
        </div>
      </div>

      {(selectedIntegration === 'Jira' ||
        selectedIntegration === 'ServiceNow' ||
        selectedIntegration === 'Azure Dev Ops') && (
        <div className="space-y-4">
          <div className="flex gap-4">
            <FormControl sx={{ width: '30%' }}>
              <InputLabel id="log-type-label">Select Log Type</InputLabel>
              <Select
                labelId="log-type-label"
                name="logType"
                value={issueObj.logType}
                onChange={handleChange}
                label="Select Log Type"
                fullWidth
              >
                <MenuItem value="Task">Log Task</MenuItem>
                <MenuItem value="Bug">Log Bug</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ width: '30%' }}>
              <InputLabel id="log-type-Level">Select Log Level</InputLabel>
              <Select
                labelId="log-type-Level"
                name="logLevel"
                value={issueObj.logLevel}
                onChange={handleChange}
                label="Select Log Level"
                fullWidth
              >
                <MenuItem value="CaseLevel">Test Case</MenuItem>
                <MenuItem value="SuitLevel">Test Suite</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ width: '30%' }}>
              <InputLabel id="log-type-logId">Select Issue Type</InputLabel>
              <Select
                labelId="log-type-logId"
                name="logId"
                value={issueObj.logId}
                onChange={handleChange}
                label="Select Issue Type"
                fullWidth
              >
                <MenuItem value="">
                  <em>Select Issue Type</em>
                </MenuItem>
                <MenuItem value="new">New Issue-Id</MenuItem>
                <MenuItem value="existing">Provide Issue-ID</MenuItem>
              </Select>
            </FormControl>
          </div>

          {issueObj.logId && issueObj.logId !== 'new' && (
            <div className="mt-4">
              <input
                type="text"
                placeholder={selectedIntegration === 'Azure Dev Ops' ? 'WorkItemId' : 'IssueId'}
                value={workItemIds[selectedIntegration] || ''}
                onChange={(e) =>
                  setWorkItemIds((prev) => ({
                    ...prev,
                    [selectedIntegration]: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
              />
            </div>
          )}

          {issueObj.logId === 'new' && (
            <div className="space-y-4 mt-4">
              <input
                type="text"
                placeholder="Project Key"
                value={issueObj.projectKey}
                onChange={(e) => handleChange({ target: { name: 'projectKey', value: e.target.value } })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
              />
              <input
                type="text"
                placeholder="Message"
                value={issueObj.message}
                onChange={(e) => handleChange({ target: { name: 'message', value: e.target.value } })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
              />
            </div>
          )}
        </div>
      )}

      <div className="mt-6">
        <p className="text-sm text-gray-600 mb-2">CI/CD Integration URL</p>
        <input
          type="text"
          value={exceUrl}
          readOnly
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-gray-50"
        />
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={handleAddIntegration}
          disabled={isPending}
          className={`px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded cursor-pointer ${
            isPending ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isPending ? 'Adding Integration...' : 'Add Integration'}
        </button>
      </div>
    </div>
  );
};

export default IntegrationTab;
